import { Tabs as TabsPrimitive } from 'radix-ui';
import { forwardRef, useState, type HTMLAttributes, type ReactNode } from 'react';

import { cn } from '../lib/cn.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * TABS — the roles and the arrow keys are Radix's; the DEFAULTS are the design
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Follows `Button.tsx`'s five rules. `tablist` / `tab` / `tabpanel`, `aria-selected`,
 * `aria-controls`, the roving tabindex and Left/Right/Home/End all come from Radix and are not
 * re-implemented or overridden here — the APG tabs pattern is exactly the kind of keyboard
 * contract that is cheap to get 90% right and expensive to get wrong. What this file decides is
 * the shape of the API and four defaults, each of which is an accessibility choice.
 *
 * ── 1. A DATA-DRIVEN `tabs` ARRAY, NOT COMPOUND COMPONENTS ─────────────────
 * The compound form (`<Tabs.List><Tabs.Trigger value="a">`) lets a caller write a trigger with
 * no matching content, which produces a tab whose `aria-controls` points at nothing — a
 * dangling reference that no automated check in this repo catches and that a screen reader
 * reports as a broken control. One array of `{ value, label, content }` makes that unrepresentable.
 * The cost is that a caller cannot interleave arbitrary markup inside the tab strip, which is
 * a thing they should not be doing anyway: a `tablist` may only contain tabs.
 *
 * ── 2. AUTOMATIC ACTIVATION, AND WHEN TO TURN IT OFF ───────────────────────
 * Default `activationMode="automatic"`: moving focus with an arrow key selects the tab and
 * swaps the panel. This is the right default because it is fewer keystrokes and because with
 * manual activation a user can be looking at a panel that does not belong to the focused tab.
 *
 * It becomes WRONG the moment a panel is expensive. Arrowing across five tabs fires five
 * fetches, and — worse for the user doing the arrowing — the content under a screen reader's
 * virtual cursor is replaced four times on the way to the tab they wanted. If a panel loads
 * data, pass `activationMode="manual"` and the tab is selected by Enter or Space instead. The
 * APG says the same thing, and this is the one knob on this component that is genuinely a
 * per-instance decision rather than a preference.
 *
 * ── 3. THE TABLIST IS NAMED ────────────────────────────────────────────────
 * Radix does not name it, because it cannot know one. An unnamed `tablist` is announced as
 * "tab list" with nothing to say which one, and this console has pages with two (an extension's
 * settings, and the call log beneath it). `label` sets `aria-label`; `labelledBy` points at an
 * existing heading, which is better when one is already on screen. Neither is enforced by the
 * type system and axe will not flag the omission — `aria-required-attr` does not require a name
 * on a tablist — so it is asserted directly in `Tabs.test.tsx`.
 *
 * ── 4. SOMETHING IS SELECTED ON FIRST RENDER ───────────────────────────────
 * Radix's `defaultValue` falls back to `''`, which matches no tab: the strip renders with every
 * tab unselected and no panel at all. That is not a spec violation but it is a broken page, so
 * the default here is the first tab that is not disabled. Starting on a disabled tab would be
 * worse than starting on none — it is selected, its panel shows, and the roving focus order
 * cannot reach it to move away.
 *
 * ── ON `disabled`, AND WHY IT IS NOT `aria-disabled` ───────────────────────
 * `Button` argues at length that a busy control should stay focusable. A tab is the opposite
 * case and Radix's native `disabled` is kept: a roving focus group has ONE tab stop, so a
 * focusable-but-inert tab would be a stop that swallows Enter with no explanation, and unlike a
 * button there is no "and here is why" state to announce. The honest options are to omit the
 * tab or to show it enabled with the explanation inside its panel; `disabled` is provided for
 * the case where the tab must stay visible for orientation, and the consequence — a keyboard
 * user cannot land on it at all — is the reason to prefer the other two.
 */

export interface TabItem {
  /** Stable across renders: it is the identity of both the tab and its panel. */
  value: string;
  label: ReactNode;
  content: ReactNode;
  /** See the header. Prefer omitting the tab. */
  disabled?: boolean;
}

export type TabsVariant = 'underline' | 'pill';
export type TabsOrientation = 'horizontal' | 'vertical';

export interface TabsProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'defaultValue' | 'dir' | 'children'> {
  tabs: readonly TabItem[];
  /** Controlled selection. Omit for uncontrolled; pair with `onValueChange`. */
  value?: string;
  /** Uncontrolled initial selection. Defaults to the first enabled tab — see the header. */
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /** Names the tablist. Pass this or `labelledBy`; see the header for why it matters. */
  label?: string;
  /** id of an existing heading that names the tablist. Wins over `label` when both are given. */
  labelledBy?: string;
  /**
   * `vertical` swaps the arrow keys to Up/Down and sets `aria-orientation`, which is what tells
   * the screen reader which keys to advertise. Setting one without the other is the usual bug;
   * Radix does both from this single prop.
   */
  orientation?: TabsOrientation;
  activationMode?: 'automatic' | 'manual';
  /** `rtl` reverses what Left and Right mean, which is the correct behaviour, not a mirror bug. */
  dir?: 'ltr' | 'rtl';
  variant?: TabsVariant;
  /**
   * Keeps every panel mounted and hides the inactive ones with the `hidden` attribute, so a
   * half-filled form in one tab survives a look at another. Off by default: an unmounted panel
   * is cheaper and cannot hold a stale subscription.
   */
  keepMounted?: boolean;
  listClassName?: string;
  /** Lands on every panel. `className` itself lands on the root. */
  panelClassName?: string;
}

export const Tabs = forwardRef<HTMLDivElement, TabsProps>(function Tabs(
  {
    tabs,
    value,
    defaultValue,
    onValueChange,
    label,
    labelledBy,
    orientation = 'horizontal',
    activationMode = 'automatic',
    dir,
    variant = 'underline',
    keepMounted = false,
    listClassName,
    panelClassName,
    className,
    ...rest
  },
  ref,
) {
  /**
   * Radix would happily manage the uncontrolled case itself, but it manages it PRIVATELY —
   * there is no way to read the current value back out. `keepMounted` needs it, because Radix's
   * own `forceMount` renders every panel with `hidden={false}` (it exists for animation
   * libraries, which then own visibility), so the correct `hidden` has to be supplied from
   * here. Mirroring the state is the price of that; the component below is always controlled
   * from this variable, whether or not the CALLER is controlling it.
   */
  const fallback = tabs.find((tab) => tab.disabled !== true)?.value ?? tabs[0]?.value ?? '';
  const [uncontrolled, setUncontrolled] = useState(defaultValue ?? fallback);
  const active = value ?? uncontrolled;
  const isVertical = orientation === 'vertical';

  const handleValueChange = (next: string): void => {
    if (value === undefined) setUncontrolled(next);
    onValueChange?.(next);
  };

  const listClasses =
    variant === 'pill'
      ? cn(
          'inline-flex gap-1 rounded-lg bg-surface-sunken p-1',
          isVertical ? 'flex-col items-stretch' : 'items-center',
        )
      : cn(
          'flex gap-1',
          isVertical
            ? 'flex-col items-stretch border-r border-border'
            : // `overflow-x-auto` and not a wrap: a tab strip that wraps to two lines breaks the
              // one-row mental model the arrow keys imply. Scrolling is safe here because the
              // region's contents are focusable, so it is reachable without a pointer.
              'items-center overflow-x-auto border-b border-border',
        );

  const triggerClasses =
    variant === 'pill'
      ? cn(
          'inline-flex min-h-8 items-center justify-center rounded-md px-3 py-1.5',
          'text-sm font-medium whitespace-nowrap text-fg-muted transition-colors',
          'hover:text-fg disabled:pointer-events-none disabled:opacity-50',
          // The selected pill differs by LUMINANCE (a filled block) and by weight, not by hue —
          // it is still legible printed in greyscale, which is what 1.4.1 is really asking.
          'data-[state=active]:bg-accent data-[state=active]:font-semibold data-[state=active]:text-accent-fg',
        )
      : cn(
          'inline-flex min-h-10 items-center px-3 py-2',
          'text-sm font-medium whitespace-nowrap text-fg-muted transition-colors',
          'hover:text-fg disabled:pointer-events-none disabled:opacity-50',
          'border-transparent data-[state=active]:border-accent data-[state=active]:text-accent',
          // A 2px rule, drawn over the list's own 1px border. Shape, not just colour: the
          // selected tab is identifiable with the palette thrown away entirely.
          isVertical ? '-mr-px justify-start border-r-2' : '-mb-px justify-center border-b-2',
        );

  return (
    <TabsPrimitive.Root
      ref={ref}
      value={active}
      onValueChange={handleValueChange}
      orientation={orientation}
      activationMode={activationMode}
      {...(dir === undefined ? {} : { dir })}
      className={cn('flex', isVertical ? 'flex-row gap-4' : 'flex-col gap-3', className)}
      {...rest}
    >
      <TabsPrimitive.List
        {...(labelledBy === undefined
          ? label === undefined
            ? {}
            : { 'aria-label': label }
          : { 'aria-labelledby': labelledBy })}
        className={cn(listClasses, listClassName)}
      >
        {tabs.map((tab) => (
          <TabsPrimitive.Trigger
            key={tab.value}
            value={tab.value}
            disabled={tab.disabled ?? false}
            className={triggerClasses}
          >
            {tab.label}
          </TabsPrimitive.Trigger>
        ))}
      </TabsPrimitive.List>

      {tabs.map((tab) => (
        <TabsPrimitive.Content
          key={tab.value}
          value={tab.value}
          /**
           * Radix gives every panel `tabIndex={0}`. That is correct and is not overridden: a
           * panel of prose has nothing tabbable in it, and without a tab stop a keyboard user
           * arriving from the tab strip has no way to scroll or read it.
           *
           * `hidden` is supplied only under `keepMounted`, where `forceMount` has taken Radix's
           * own visibility handling away. Note it must be the `hidden` ATTRIBUTE and not a
           * `display:none` class — `hidden` is what removes the panel from the accessibility
           * tree, and a CSS-only hide would leave three panels' worth of text in the reading
           * order of a page that appears to show one.
           */
          {...(keepMounted ? { forceMount: true as const, hidden: tab.value !== active } : {})}
          className={cn('min-w-0 text-fg', isVertical && 'flex-1', panelClassName)}
        >
          {tab.content}
        </TabsPrimitive.Content>
      ))}
    </TabsPrimitive.Root>
  );
});
